import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Shield, RotateCcw, Check, X, Search, ShieldAlert } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { MODULE_PERMISSIONS_UPDATED } from "@/lib/clinicModuleAccess";

// =====================================================================
// Settings → Permissions (V2 Round 4)
//
// 4-role × N-key matrix. Rows = permission keys grouped by category;
// columns = clinic_admin / doctor / receptionist / assistant. Each cell
// is a clickable allow/deny toggle. Cells that differ from the platform
// default get a small dot indicator.
//
// Calls:
//   list_permissions_matrix()
//   list_module_permissions_matrix()
//   set_permission(role, key, allowed)
//   reset_permissions_to_defaults()
//
// All gated on clinic.settings.manage. The page itself is reachable
// from the rail; if the caller lacks the permission, the RPC raises
// 42501 and the page renders an "Access denied" notice.
// =====================================================================

const ROLES = [
  { key: "clinic_admin", label: "Clinic Admin" },
  { key: "doctor",       label: "Dentist"      },
  { key: "receptionist", label: "Front Desk"   },
  { key: "assistant",    label: "Assistant"    },
];

const HIGH_IMPACT_PERMISSIONS = new Set([
  "patient.delete",
  "visit.price_override",
  "payment.allocate",
  "payment_request.cancel",
  "ortho_case.override_close_with_balance",
  "clinic.settings.manage",
]);

// Hand-curated catalog: groups, labels, descriptions. Order in this
// array drives the on-screen order.
const CATALOG = [
  { group: "Module access", prominent: true, items: [
    { key: "module.chairside.view",           label: "Chairside",           desc: "Show Chairside in the menu and allow the role to open it." },
    { key: "module.treatment_plans.view",     label: "Treatment plans",     desc: "Show Treatment Plans in the menu and allow the role to open it." },
    { key: "module.ortho.view",               label: "Ortho",               desc: "Show Ortho in the menu and allow the role to open it." },
    { key: "module.checkout.view",            label: "Checkout",            desc: "Show Checkout in the menu and allow the role to open it." },
    { key: "module.accounts_receivable.view", label: "Accounts Receivable", desc: "Show Accounts Receivable in the menu and allow the role to open it." },
    { key: "module.hmo_claims.view",          label: "HMO Claims",          desc: "Show HMO Claims in the menu and allow the role to open it." },
    { key: "module.inventory.view",           label: "Inventory",           desc: "Allow the role to view current stock and recorded changes. Only Admin can edit stock setup." },
    { key: "module.reports.view",             label: "Reports",             desc: "Show Reports in the menu and allow the role to open it." },
    { key: "module.expenses.view",            label: "Expenses",            desc: "Show Expenses in the menu and allow the role to open it." },
    { key: "module.support.view",             label: "Support",             desc: "Show Support in the menu and the support chat button." },
  ]},
  { group: "Patients", items: [
    { key: "patient.create",              label: "Create patients",                   desc: "Add a new patient record." },
    { key: "patient.delete",              label: "Permanently remove patients",       desc: "Exceptional cleanup only. Patients should normally be deactivated so their history stays intact." },
    { key: "patient.activate",            label: "Activate patients",                 desc: "Move a patient from Inactive back to Active." },
    { key: "patient.deactivate",          label: "Deactivate patients",               desc: "Move an Active patient to Inactive." },
    { key: "patient.edit_contact",        label: "Edit contact info",                 desc: "Update name, phone, email, address." },
    { key: "patient.recall.update",       label: "Edit recall plan",                  desc: "Change recall cadence on a patient." },
    { key: "patient.toggle_recall_opt_out", label: "Toggle recall opt-out",           desc: "Pause or resume recall reminders." },
    { key: "patient.household.move",      label: "Move between households",           desc: "Reassign a patient to a different family group." },
  ]},
  { group: "Appointments", items: [
    { key: "appointment.book",            label: "Book appointments",                 desc: "Create a new appointment." },
    { key: "appointment.confirm",         label: "Confirm",                           desc: "Mark a Scheduled appointment as Confirmed." },
    { key: "appointment.check_in",        label: "Check in",                          desc: "Mark a patient as arrived." },
    { key: "appointment.no_show",         label: "Mark no-show",                      desc: "Manually flag an appointment as a no-show." },
    { key: "appointment.cancel",          label: "Cancel",                            desc: "Cancel a Scheduled or Confirmed appointment." },
    { key: "appointment.reschedule",      label: "Reschedule",                        desc: "Move an appointment to a new time / chair / doctor." },
    { key: "appointment.quick_check_in",  label: "Quick check-in (walk-in)",          desc: "Create + check in a walk-in patient in one step." },
  ]},
  { group: "Charting", items: [
    { key: "chart.write",                 label: "Edit chart drafts",                 desc: "Set, change, or remove findings on the dental chart." },
    { key: "chart.manage_findings",       label: "Manage finding catalog",            desc: "Add or edit custom findings beyond the built-in findings." },
    { key: "chart.finalize",              label: "Finalize chart",                    desc: "Commit a chart draft to a permanent snapshot." },
  ]},
  { group: "Treatment plans", items: [
    { key: "treatment_plan.propose",      label: "Propose plans",                     desc: "Create a Draft and move it to Proposed." },
    { key: "treatment_plan.accept",       label: "Accept on patient's behalf",        desc: "Mark items Accepted. Normally a doctor or receptionist action." },
    { key: "treatment_plan.reject",       label: "Reject items",                      desc: "Mark items Declined." },
    { key: "treatment_plan.supersede",    label: "Revise plan",                       desc: "Create a new Draft from a Proposed plan; old plan becomes Superseded." },
    { key: "treatment_plan.delete_draft", label: "Delete drafts",                     desc: "Remove a Draft plan that hasn't been Proposed yet." },
    { key: "treatment_plan.import_existing", label: "Import ongoing treatment",       desc: "Record a multi-visit case that started before Dental OS, including its opening balance." },
  ]},
  { group: "Treatment needs", items: [
    { key: "treatment_need.schedule",     label: "Schedule needs into appointments",  desc: "Link an Accepted plan item to a booking." },
    { key: "treatment_need.unschedule",   label: "Unschedule",                        desc: "Remove a need from its appointment without cancelling." },
    { key: "treatment_need.decline",      label: "Decline mid-flight",                desc: "Mark a previously-Accepted need as Declined." },
    { key: "treatment_need.complete_via_checkout", label: "Complete during checkout", desc: "Receive payment and mark a need Completed at the same time." },
  ]},
  { group: "Visits & checkout", items: [
    { key: "visit.complete_checkout",     label: "Complete checkout",                 desc: "Complete the visit, receipt, and payment together." },
    { key: "visit.price_override",        label: "Override pricing at checkout",      desc: "Change a fee away from the procedure default during checkout." },
  ]},
  { group: "Payments", items: [
    { key: "payment.allocate",            label: "Allocate payments",                 desc: "Move credit between charges or to a different invoice." },
    { key: "insurance_claim.settle",      label: "Settle HMO claims",                 desc: "Mark HMO claims submitted, paid, partially paid, or denied." },
    { key: "payment_request.cancel",      label: "Cancel payment requests",           desc: "Void an outstanding payment-request link." },
  ]},
  { group: "Ortho", items: [
    { key: "ortho_case.activate",         label: "Activate ortho case",               desc: "Move a Proposed ortho case to Active." },
    { key: "ortho_case.pause_clinical",   label: "Pause clinically",                  desc: "Suspend an active case for medical reasons." },
    { key: "ortho_case.resume_clinical",  label: "Resume clinically",                 desc: "Move a paused case back to active." },
    { key: "ortho_case.mark_near_completion", label: "Mark near completion",          desc: "Flag a case as approaching completion." },
    { key: "ortho_case.complete",         label: "Complete ortho case",               desc: "Close out an ortho case with no balance." },
    { key: "ortho_case.override_close_with_balance", label: "Force-close with balance", desc: "Close an ortho case that still has an outstanding balance. This is an Admin override." },
  ]},
  { group: "Medical history", items: [
    { key: "medical_history.verify",      label: "Verify medical history",            desc: "Mark a submitted MH form as reviewed." },
  ]},
  { group: "Staff & settings", items: [
    { key: "staff.invite",                label: "Invite & manage staff",             desc: "Send invitations, change roles, remove members." },
    { key: "clinic.settings.manage",      label: "Manage clinic settings",            desc: "Edit Letterhead, Hours, Chairs, Services, Permissions, etc. Required to use this page." },
    { key: "clinic.insurance.manage",     label: "Manage insurance payers",           desc: "Add, edit, archive HMOs the clinic accepts. Receptionists usually need this." },
  ]},
];

export default function SettingsPermissions() {
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [matrix, setMatrix]     = useState({}); // { 'role:key' → { allowed, platform_default } }
  const [savingCells, setSaving] = useState({}); // { 'role:key' → true }
  const [resetting, setResetting] = useState(false);
  const [view, setView] = useState("sections");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const [actionResult, moduleResult] = await Promise.all([
      supabase.rpc("list_permissions_matrix"),
      supabase.rpc("list_module_permissions_matrix"),
    ]);
    setLoading(false);
    const e = actionResult.error || moduleResult.error;
    if (e) { setError(e.message ?? "Could not load permissions."); return; }
    const m = {};
    [...(actionResult.data ?? []), ...(moduleResult.data ?? [])].forEach((row) => {
      m[`${row.role}:${row.permission_key}`] = {
        allowed: row.allowed,
        platform_default: row.platform_default,
      };
    });
    setMatrix(m);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleToggle(role, key) {
    const cellKey = `${role}:${key}`;
    const current = matrix[cellKey];
    if (!current) return;
    const nextAllowed = !current.allowed;
    if (HIGH_IMPACT_PERMISSIONS.has(key)) {
      const roleLabel = ROLES.find((item) => item.key === role)?.label || role;
      const item = CATALOG.flatMap((group) => group.items).find((permission) => permission.key === key);
      const action = nextAllowed ? "allow" : "remove";
      if (!window.confirm(`${action === "allow" ? "Allow" : "Remove"} ${roleLabel} access to “${item?.label || key}”? This can affect patient, payment, or clinic records.`)) return;
    }
    setSaving((s) => ({ ...s, [cellKey]: true }));
    const { error: e } = await supabase.rpc("set_permission", {
      p_role: role, p_permission_key: key, p_allowed: nextAllowed,
    });
    setSaving((s) => { const n = { ...s }; delete n[cellKey]; return n; });
    if (e) {
      toast.error(e.message ?? "Could not update permission.");
      return;
    }
    setMatrix((m) => ({ ...m, [cellKey]: { ...current, allowed: nextAllowed } }));
    window.dispatchEvent(new CustomEvent(MODULE_PERMISSIONS_UPDATED, {
      detail: { role, permissionKey: key, allowed: nextAllowed },
    }));
  }

  async function handleResetAll() {
    if (!window.confirm("Reset every permission back to the platform defaults? Any custom overrides for this clinic will be cleared.")) return;
    setResetting(true);
    const { error: e } = await supabase.rpc("reset_permissions_to_defaults");
    setResetting(false);
    if (e) { toast.error(e.message ?? "Could not reset."); return; }
    toast.success("Permissions reset to platform defaults.");
    await load();
    window.dispatchEvent(new CustomEvent(MODULE_PERMISSIONS_UPDATED, {
      detail: { reset: true },
    }));
  }

  const overrideCount = useMemo(
    () => Object.values(matrix).filter((c) => c.allowed !== c.platform_default).length,
    [matrix]
  );
  const visibleGroups = useMemo(() => {
    const source = view === "sections" ? CATALOG.slice(0, 1) : CATALOG.slice(1);
    const needle = query.trim().toLowerCase();
    if (!needle) return source;
    return source.map((group) => ({
      ...group,
      items: group.items.filter((item) => [item.label, item.desc].some((value) => value.toLowerCase().includes(needle))),
    })).filter((group) => group.items.length > 0);
  }, [query, view]);

  // Access-denied: list_permissions_matrix raises 42501 if the caller
  // doesn't hold clinic.settings.manage. Surface a friendly state.
  if (error && /permission denied/i.test(error)) {
    return (
      <div data-testid="settings-permissions-denied" className="bg-white rounded-2xl shadow-sm p-8 text-center">
        <Shield className="w-8 h-8 mx-auto text-slate-300" />
        <p className="text-sm text-slate-600 mt-3">You need <code className="text-xs bg-slate-100 px-1 rounded">clinic.settings.manage</code> to view this page.</p>
        <p className="text-xs text-slate-400 mt-2">Ask a Clinic Admin to grant access.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="settings-permissions" data-tutorial-id="settings-permissions-page">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Permissions</h1>
            <p className="text-xs text-slate-500">
              Start with the sections each role can open. Detailed actions are available when the clinic needs finer control.
              {overrideCount > 0 ? (
                <span className="ml-1 text-amber-700">
                  {overrideCount} override{overrideCount === 1 ? "" : "s"} away from platform defaults.
                </span>
              ) : (
                <span className="ml-1 text-slate-400">All cells match platform defaults.</span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={handleResetAll}
            disabled={resetting || loading}
            data-testid="permissions-reset"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded disabled:opacity-50">
            {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Use recommended defaults
          </button>
        </div>
      </div>

      {/* Error (non-permission) */}
      {error && !/permission denied/i.test(error) ? (
        <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
      ) : null}

      {/* Loading */}
      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="inline-flex rounded-xl bg-slate-100 p-1" aria-label="Permission level">
              <button
                type="button"
                onClick={() => { setView("sections"); setQuery(""); }}
                data-testid="permissions-view-sections"
                className={`min-h-9 rounded-lg px-3 text-sm font-semibold ${view === "sections" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600"}`}
              >
                Sections
              </button>
              <button
                type="button"
                onClick={() => { setView("actions"); setQuery(""); }}
                data-testid="permissions-view-actions"
                className={`min-h-9 rounded-lg px-3 text-sm font-semibold ${view === "actions" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600"}`}
              >
                Detailed actions
              </button>
            </div>
            <label className="relative block w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={view === "sections" ? "Find a section" : "Find an action"}
                aria-label="Find a permission"
                className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-3 text-sm outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
              />
            </label>
          </div>

          {view === "actions" ? (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <p>Most clinics should keep the recommended defaults. Change these only when a staff role needs a specific task.</p>
            </div>
          ) : null}

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="permissions-matrix" data-tutorial-id="settings-permissions-matrix">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-2">
                  Permission
                </th>
                {ROLES.map((r) => (
                  <th key={r.key} className="text-center text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-3 py-2 w-28">
                    {r.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleGroups.map((group) => (
                <Fragment key={group.group}>
                  <tr className={group.prominent ? "bg-teal-50" : "bg-slate-50/40"}>
                    <td colSpan={1 + ROLES.length}
                        className={`text-[10px] uppercase tracking-wider font-semibold px-4 py-1.5 border-t ${group.prominent ? "border-teal-100 text-teal-700" : "border-slate-100 text-slate-400"}`}>
                      {group.group}
                    </td>
                  </tr>
                  {group.items.map((item) => (
                    <tr key={item.key} className="border-t border-slate-100 hover:bg-slate-50/40">
                      <td className="px-4 py-2 align-top">
                        <p className="text-sm text-slate-800 font-medium">{item.label}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>
                      </td>
                      {ROLES.map((role) => {
                        const cellKey = `${role.key}:${item.key}`;
                        const cell    = matrix[cellKey];
                        const saving  = !!savingCells[cellKey];
                        const overridden = cell && cell.allowed !== cell.platform_default;
                        return (
                          <td key={cellKey} className="px-3 py-2 text-center align-middle">
                            <button
                              type="button"
                              onClick={() => handleToggle(role.key, item.key)}
                              disabled={saving || !cell}
                              data-testid={`perm-cell-${role.key}-${item.key}`}
                              title={cell ? `Platform default: ${cell.platform_default ? "Allow" : "Deny"}${overridden ? " (overridden)" : ""}` : ""}
                              className={`relative inline-flex items-center justify-center w-9 h-9 rounded-lg transition disabled:opacity-50 ${
                                cell?.allowed
                                  ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                  : "bg-rose-50 text-rose-700 hover:bg-rose-100"
                              }`}
                            >
                              {saving ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : cell?.allowed ? (
                                <Check className="w-4 h-4" strokeWidth={2.5} />
                              ) : (
                                <X className="w-4 h-4" strokeWidth={2.5} />
                              )}
                              {overridden ? (
                                <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-amber-500" />
                              ) : null}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-500 flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" /> = differs from platform default</span>
          </div>
        </div>
        </div>
      )}
    </div>
  );
}
